import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

/**
 * Network Graph Component
 * Creates an interactive force-directed graph visualization for node networks
 * with improved error handling and user interaction
 */
const NetworkGraph = ({ data, onNodeClick }) => {
  const svgRef = useRef(null);
  
  useEffect(() => {
    // Validate data before proceeding
    if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
      console.warn('Invalid or empty data provided to NetworkGraph');
      return;
    }
    
    const width = 800;
    const height = 600;
    
    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();
    
    // Create the SVG container
    const svg = d3.select(svgRef.current)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("class", "network-graph");
    
    // Add a background to capture zoom events better
    svg.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent");
    
    // Create a container for the zoomable content
    const container = svg.append("g")
      .attr("class", "container");
    
    // Create the simulation
    const simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links).id(d => d.id).distance(100))
      .force("charge", d3.forceManyBody().strength(-400))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(60));
    
    // Define color scale for node types
    const colorScale = d3.scaleOrdinal()
      .domain(['root', 'subordinate'])
      .range(['#4F46E5', '#10B981']);
    
    // Define color scale for node levels
    const levelColorScale = d3.scaleOrdinal()
      .domain([0, 1, 2])
      .range(['#4F46E5', '#10B981', '#F97316']);
    
    // Define node size scale based on tier
    const nodeSizeScale = d3.scaleOrdinal()
      .domain(['Standard', 'Super', 'Master', 'Apex'])
      .range([35, 45, 55, 65]);
    
    // Create a group for the links
    const link = container.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .enter()
      .append("line")
      .attr("class", d => `link level-${d.level}`)
      .attr("stroke", d => d.level === 1 ? "#10B981" : "#F97316")
      .attr("stroke-width", d => d.level === 1 ? 3 : 2)
      .attr("stroke-opacity", 0.6);
    
    // Create a group for the nodes
    const node = container.append("g")
      .attr("class", "nodes")
      .selectAll(".node")
      .data(data.nodes)
      .enter()
      .append("g")
      .attr("class", d => `node ${d.type === 'root' ? 'root' : `level-${d.level}`}`)
      .on("click", (event, d) => {
        event.stopPropagation();
        if (onNodeClick) onNodeClick(d);
      })
      .call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended));
    
    // Add circles to the nodes
    node.append("circle")
      .attr("r", d => nodeSizeScale(d.tier) || 35)
      .attr("fill", d => d.type === 'root' ? colorScale(d.type) : levelColorScale(d.level))
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2);
    
    // Add text labels to the nodes
    node.append("text")
      .attr("dy", ".35em")
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff")
      .attr("font-weight", "bold")
      .attr("font-size", d => d.type === 'root' ? "14px" : "12px")
      .text(d => {
        // Truncate long labels
        const maxLength = 10;
        return d.label.length > maxLength ? d.label.substring(0, maxLength) + "..." : d.label;
      })
      .attr("pointer-events", "none");
    
    // Add tooltips with additional information
    node.append("title")
      .text(d => `${d.label} (${d.tier})\nType: ${d.type}\nLevel: ${d.level}`);
    
    // Define the tick function to update positions
    simulation.on("tick", () => {
      // Keep nodes within bounds
      data.nodes.forEach(d => {
        d.x = Math.max(60, Math.min(width - 60, d.x));
        d.y = Math.max(60, Math.min(height - 60, d.y));
      });
      
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);
      
      node
        .attr("transform", d => `translate(${d.x},${d.y})`);
    });
    
    // Define drag functions with error handling
    function dragstarted(event, d) {
      try {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      } catch (error) {
        console.error("Error in dragstarted:", error);
      }
    }
    
    function dragged(event, d) {
      try {
        d.fx = event.x;
        d.fy = event.y;
      } catch (error) {
        console.error("Error in dragged:", error);
      }
    }
    
    function dragended(event, d) {
      try {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      } catch (error) {
        console.error("Error in dragended:", error);
      }
    }
    
    // Add zoom capabilities
    const zoom = d3.zoom()
      .scaleExtent([0.3, 5])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });
    
    svg.call(zoom);
    
    // Add double-click to reset zoom
    svg.on("dblclick.zoom", () => {
      svg.transition()
        .duration(750)
        .call(zoom.transform, d3.zoomIdentity);
    });
    
    // Center the root node and apply initial zooming for better visibility
    const rootNode = data.nodes.find(n => n.type === 'root');
    if (rootNode) {
      rootNode.fx = width / 2;
      rootNode.fy = height / 2;
      
      // Apply automatic initial zoom based on network size
      if (data.nodes.length > 10) {
        const scale = Math.max(0.5, 1 - data.nodes.length * 0.02);
        svg.transition()
          .duration(750)
          .call(zoom.transform, d3.zoomIdentity.scale(scale).translate(width/2, height/2));
      }
      
      // Release the fixed position after initial positioning
      setTimeout(() => {
        rootNode.fx = null;
        rootNode.fy = null;
      }, 2000);
    }
    
    // Clean up
    return () => {
      simulation.stop();
    };
  }, [data, onNodeClick]);
  
  return (
    <div className="network-graph-container">
      <div className="network-graph-controls">
        <div className="zoom-controls">
          <button className="zoom-btn" title="Zoom In">+</button>
          <button className="zoom-btn" title="Zoom Out">-</button>
          <button className="zoom-btn" title="Reset">⟳</button>
        </div>
        <div className="network-graph-help">
          Double-click: Reset view | Drag: Move node | Scroll: Zoom
        </div>
      </div>
      
      <svg ref={svgRef} className="network-graph-svg"></svg>
      
      <div className="network-graph-legend">
        <div className="legend-title">Legend</div>
        <div className="legend-item">
          <div className="legend-color root"></div>
          <span>Your Node</span>
        </div>
        <div className="legend-item">
          <div className="legend-color level-1"></div>
          <span>Level 1 (Direct)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color level-2"></div>
          <span>Level 2 (Indirect)</span>
        </div>
      </div>
    </div>
  );
};

export default NetworkGraph;
